import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import type { TeamQuery } from 'generated/resources/graphql'

import Popover, {
  type PopoverMenuTouchableOptionProps as Option,
} from 'modules/core/components/PopoverMenu'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import membershipsCollection from 'modules/teams/store/collections/memberships'

type TeamMembership = NonNullable<NonNullable<TeamQuery['teams']['team']>['memberships']>[number]

interface Props {
  membership: TeamMembership
  canManageTeams: boolean
  hasDevJobCatalogTree: boolean
}

const Leader = ({ membership, canManageTeams, hasDevJobCatalogTree }: Props) => {
  const employee = membership.employee
  const jobCatalogLevel = employee.referenceContract?.jobCatalogLevel
  const jobTitle = employee.referenceContract?.jobTitle
  const role = jobCatalogLevel?.role

  // Compute the job display - prefer jobTitle, then role + level
  let job = ''
  if (jobTitle) {
    job = jobTitle
  } else if (role && !role.archived) {
    const levelName = jobCatalogLevel?.isDefault ? '' : jobCatalogLevel?.name
    job = role.name && levelName ? `${role.name}, ${levelName}` : role.name
  }
  const { navigation } = useNavigation()

  const onClickMemberDemote = () => {
    // Find the mobx-rest membership model to perform the action
    const membershipModel = membershipsCollection.get(membership.id)
    if (membershipModel) {
      asyncAction(() =>
        membershipModel.save({
          lead: false,
        })
      )
    }
  }

  const onClickMemberRemove = () => {
    // Find the mobx-rest membership model to perform the action
    const membershipModel = membershipsCollection.get(membership.id)
    if (membershipModel) {
      asyncAction(() => membershipModel.destroy())
    }
  }

  const options: Option[] = [
    {
      title: i18n.t('teams.change_to_member'),
      onClick: onClickMemberDemote,
    },
    {
      title: i18n.t('teams.leaders.remove_title'),
      onClick: onClickMemberRemove,
      danger: true,
    },
  ]

  return (
    <Box
      padding="s12"
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
      style={{ maxWidth: '100%' }}
      borderRadius={{
        all: 'abs008',
      }}
    >
      <Box width="full" gap="s16" flexDirection="row" justifyContent="spaceBetween">
        <Box justifyContent="flexStart" flexDirection="row" alignItems="center">
          <UserAvatar
            user={{
              fullName: `${employee.firstName} ${employee.lastName}`,
              avatar: employee.avatar?.url ?? '',
            }}
            size="s40"
          />
          <Box paddingLeft="s24" alignItems="flexStart">
            <Text.Normal weight="medium" color="grey1000">
              {getEmployeeDisplayName({
                fullName: `${employee.firstName} ${employee.lastName}`,
                lastName: employee.lastName,
                preferredName: employee.preferredName ?? undefined,
              })}
            </Text.Normal>
            {!hasDevJobCatalogTree && (
              <Text.Normal size="200" color="grey800" weight="medium">
                {job}
              </Text.Normal>
            )}
          </Box>
        </Box>
        <Box alignItems="center" justifyContent="flexEnd" flexDirection="row" gap="s12">
          {canManageTeams && (
            <Popover type="list">
              <Popover.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </Popover.Trigger>
              <Popover.Content side="bottom" align="center">
                {options.map(({ title, description, onClick, danger }, index) => (
                  <Popover.TouchableOption
                    key={index}
                    danger={danger}
                    title={title}
                    onClick={onClick}
                    description={description}
                  />
                ))}
              </Popover.Content>
            </Popover>
          )}
          <Button.Router
            to={navigation.employees.id.fullPath({ id: employee.id })}
            type="secondary"
            iconName="arrow-right"
          />
        </Box>
      </Box>
    </Box>
  )
}

export default Leader
